export interface AdminControlsSettings {
    logoUrl: string;
    logoAlt: string;
    mobileLogoUrl: string;
    logoScale: number;
    logoPositionX: number;
    logoPositionY: number;
    mobileLogoScale: number;
    mobileLogoPositionX: number;
    mobileLogoPositionY: number;
    tvLogoUrl: string;
    tvLogoScale: number;
    tvLogoPositionX: number;
    tvLogoPositionY: number;
    compactLogoUrl: string;
    qrLogoUrl: string;
    qrLogoBackground: "transparent" | "light" | "dark";
    socialImageUrl: string;
    maintenanceMode: boolean;
    guestAccessEnabled: boolean;
    publicDiscoveryEnabled: boolean;
    paymentsEnabled: boolean;
    announcementsEnabled: boolean;
    announcementText: string;
}

type PlatformSettingsApi = {
    logo_url: string; logo_alt: string; compact_logo_url?: string; qr_logo_url?: string;
    mobile_logo_url?: string; logo_scale?: number; logo_position_x?: number; logo_position_y?: number;
    mobile_logo_scale?: number; mobile_logo_position_x?: number; mobile_logo_position_y?: number;
    tv_logo_url?: string; tv_logo_scale?: number; tv_logo_position_x?: number; tv_logo_position_y?: number;
    qr_logo_background?: "transparent" | "light" | "dark"; social_image_url?: string; maintenance_mode: boolean;
    guest_access_enabled: boolean; public_discovery_enabled: boolean;
    payments_enabled: boolean; announcements_enabled: boolean; announcement_text: string;
};

export const ADMIN_CONTROLS_STORAGE_KEY = "qit:admin-controls";
export const ADMIN_CONTROLS_EVENT = "qit:admin-controls-updated";

export const getPlatformLogoViewBox = (scale: number, positionX: number, positionY: number): string => {
    const width = 560 * (100 / scale);
    const height = 240 * (100 / scale);
    const centerX = 500 + ((positionX - 50) / 50) * 220;
    const centerY = 265 + ((positionY - 50) / 50) * 145;
    return `${centerX - width / 2} ${centerY - height / 2} ${width} ${height}`;
};

export const defaultAdminControlsSettings: AdminControlsSettings = {
    logoUrl: "/logo.jpg?v=3",
    logoAlt: "QIt Playlist Home",
    mobileLogoUrl: "",
    logoScale: 100,
    logoPositionX: 50,
    logoPositionY: 50,
    mobileLogoScale: 100,
    mobileLogoPositionX: 50,
    mobileLogoPositionY: 50,
    tvLogoUrl: "",
    tvLogoScale: 100,
    tvLogoPositionX: 50,
    tvLogoPositionY: 50,
    compactLogoUrl: "/favicon.png?v=6",
    qrLogoUrl: "/Qit_WHITE_LOGO_NEW.png",
    qrLogoBackground: "transparent",
    socialImageUrl: "/logo.jpg?v=3",
    maintenanceMode: false,
    guestAccessEnabled: true,
    publicDiscoveryEnabled: true,
    paymentsEnabled: true,
    announcementsEnabled: false,
    announcementText: "",
};

const coerceBoolean = (value: unknown, fallback: boolean): boolean =>
    typeof value === "boolean" ? value : fallback;

const coerceString = (value: unknown, fallback: string): string => {
    if (typeof value !== "string") return fallback;
    const trimmed = value.trim();
    return trimmed || fallback;
};

const coerceRange = (value: unknown, fallback: number, minimum: number, maximum: number): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.min(maximum, Math.max(minimum, Math.round(parsed))) : fallback;
};

export const normalizeAdminControlsSettings = (raw: unknown): AdminControlsSettings => {
    const source = raw && typeof raw === "object" ? (raw as Partial<AdminControlsSettings>) : {};

    return {
        logoUrl: coerceString(source.logoUrl, defaultAdminControlsSettings.logoUrl),
        logoAlt: coerceString(source.logoAlt, defaultAdminControlsSettings.logoAlt),
        mobileLogoUrl: typeof source.mobileLogoUrl === "string" ? source.mobileLogoUrl.trim() : "",
        logoScale: coerceRange(source.logoScale, 100, 50, 200),
        logoPositionX: coerceRange(source.logoPositionX, 50, 0, 100),
        logoPositionY: coerceRange(source.logoPositionY, 50, 0, 100),
        mobileLogoScale: coerceRange(source.mobileLogoScale, 100, 50, 200),
        mobileLogoPositionX: coerceRange(source.mobileLogoPositionX, 50, 0, 100),
        mobileLogoPositionY: coerceRange(source.mobileLogoPositionY, 50, 0, 100),
        tvLogoUrl: typeof source.tvLogoUrl === "string" ? source.tvLogoUrl.trim() : "",
        tvLogoScale: coerceRange(source.tvLogoScale, 100, 50, 200),
        tvLogoPositionX: coerceRange(source.tvLogoPositionX, 50, 0, 100),
        tvLogoPositionY: coerceRange(source.tvLogoPositionY, 50, 0, 100),
        compactLogoUrl: coerceString(source.compactLogoUrl, source.logoUrl || defaultAdminControlsSettings.compactLogoUrl),
        qrLogoUrl: coerceString(source.qrLogoUrl, source.logoUrl || defaultAdminControlsSettings.qrLogoUrl),
        qrLogoBackground: ["transparent", "light", "dark"].includes(String(source.qrLogoBackground))
            ? source.qrLogoBackground as AdminControlsSettings["qrLogoBackground"]
            : defaultAdminControlsSettings.qrLogoBackground,
        socialImageUrl: coerceString(source.socialImageUrl, source.logoUrl || defaultAdminControlsSettings.socialImageUrl),
        maintenanceMode: coerceBoolean(source.maintenanceMode, defaultAdminControlsSettings.maintenanceMode),
        guestAccessEnabled: coerceBoolean(source.guestAccessEnabled, defaultAdminControlsSettings.guestAccessEnabled),
        publicDiscoveryEnabled: coerceBoolean(source.publicDiscoveryEnabled, defaultAdminControlsSettings.publicDiscoveryEnabled),
        paymentsEnabled: coerceBoolean(source.paymentsEnabled, defaultAdminControlsSettings.paymentsEnabled),
        announcementsEnabled: coerceBoolean(source.announcementsEnabled, defaultAdminControlsSettings.announcementsEnabled),
        announcementText: typeof source.announcementText === "string" ? source.announcementText : "",
    };
};

export const readAdminControlsSettings = (): AdminControlsSettings => {
    if (typeof window === "undefined") return defaultAdminControlsSettings;

    try {
        const raw = window.localStorage.getItem(ADMIN_CONTROLS_STORAGE_KEY);
        return normalizeAdminControlsSettings(raw ? JSON.parse(raw) : null);
    } catch {
        return defaultAdminControlsSettings;
    }
};

export const saveAdminControlsSettings = (settings: AdminControlsSettings): AdminControlsSettings => {
    const normalized = normalizeAdminControlsSettings(settings);

    if (typeof window !== "undefined") {
        window.localStorage.setItem(ADMIN_CONTROLS_STORAGE_KEY, JSON.stringify(normalized));
        window.dispatchEvent(new CustomEvent(ADMIN_CONTROLS_EVENT, { detail: normalized }));
    }

    return normalized;
};

export const resetAdminControlsSettings = (): AdminControlsSettings => {
    if (typeof window !== "undefined") {
        window.localStorage.removeItem(ADMIN_CONTROLS_STORAGE_KEY);
        window.dispatchEvent(new CustomEvent(ADMIN_CONTROLS_EVENT, { detail: defaultAdminControlsSettings }));
    }

    return defaultAdminControlsSettings;
};

export const parseAdminControlsApiResponse = (raw: PlatformSettingsApi): AdminControlsSettings =>
    normalizeAdminControlsSettings({
        logoUrl: raw.logo_url, logoAlt: raw.logo_alt, compactLogoUrl: raw.compact_logo_url,
        mobileLogoUrl: raw.mobile_logo_url, logoScale: raw.logo_scale,
        logoPositionX: raw.logo_position_x, logoPositionY: raw.logo_position_y,
        mobileLogoScale: raw.mobile_logo_scale, mobileLogoPositionX: raw.mobile_logo_position_x,
        mobileLogoPositionY: raw.mobile_logo_position_y,
        tvLogoUrl: raw.tv_logo_url, tvLogoScale: raw.tv_logo_scale,
        tvLogoPositionX: raw.tv_logo_position_x, tvLogoPositionY: raw.tv_logo_position_y,
        qrLogoUrl: raw.qr_logo_url, qrLogoBackground: raw.qr_logo_background,
        socialImageUrl: raw.social_image_url, maintenanceMode: raw.maintenance_mode,
        guestAccessEnabled: raw.guest_access_enabled, publicDiscoveryEnabled: raw.public_discovery_enabled,
        paymentsEnabled: raw.payments_enabled, announcementsEnabled: raw.announcements_enabled,
        announcementText: raw.announcement_text,
    });

export const toAdminControlsApiPayload = (settings: AdminControlsSettings): PlatformSettingsApi => ({
    logo_url: settings.logoUrl, logo_alt: settings.logoAlt, compact_logo_url: settings.compactLogoUrl,
    mobile_logo_url: settings.mobileLogoUrl, logo_scale: settings.logoScale,
    logo_position_x: settings.logoPositionX, logo_position_y: settings.logoPositionY,
    mobile_logo_scale: settings.mobileLogoScale, mobile_logo_position_x: settings.mobileLogoPositionX,
    mobile_logo_position_y: settings.mobileLogoPositionY,
    tv_logo_url: settings.tvLogoUrl, tv_logo_scale: settings.tvLogoScale,
    tv_logo_position_x: settings.tvLogoPositionX, tv_logo_position_y: settings.tvLogoPositionY,
    qr_logo_url: settings.qrLogoUrl, qr_logo_background: settings.qrLogoBackground,
    social_image_url: settings.socialImageUrl, maintenance_mode: settings.maintenanceMode,
    guest_access_enabled: settings.guestAccessEnabled, public_discovery_enabled: settings.publicDiscoveryEnabled,
    payments_enabled: settings.paymentsEnabled, announcements_enabled: settings.announcementsEnabled,
    announcement_text: settings.announcementText,
});
